import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PersonalInfoTab from './components/PersonalInfoTab';
import ContactDetailsTab from './components/ContactDetailsTab';
import ProfessionalInfoTab from './components/ProfessionalInfoTab';
import DuplicateDetectionModal from './components/DuplicateDetectionModal';
import DocumentAttachmentPanel from './components/DocumentAttachmentPanel';
import VersionHistoryPanel from './components/VersionHistoryPanel';
import IntegrationStatusPanel from './components/IntegrationStatusPanel';
import ApplicationTrackingPanel from './components/ApplicationTrackingPanel';

const ClientProfileManagement = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    // Personal Information
    salutation: 'herr',
    firstName: 'Max',
    lastName: 'Mustermann',
    birthName: '',
    dateOfBirth: '1985-03-15',
    placeOfBirth: 'Berlin',
    nationality: 'de',
    idNumber: 'L01X00T471',
    socialSecurityNumber: '12345678A123',
    maritalStatus: 'verheiratet',
    numberOfChildren: '2',
    taxId: '12345678901',
    street: 'Friedrichstraße 123',
    postalCode: '10117',
    city: 'Berlin',
    state: 'Berlin',
    addressSupplement: '',
    
    // Contact Details
    phoneNumbers: [
      { id: 1, type: 'mobile', number: '+49 170 1234567', isPrimary: true },
      { id: 2, type: 'home', number: '+49 30 9876543', isPrimary: false }
    ],
    emailAddresses: [
      { id: 1, type: 'personal', address: 'max.mustermann@email.de', isPrimary: true }
    ],
    preferredContact: 'email',
    bestTimeToContact: '9:00 - 17:00 Uhr',
    emailConsent: true,
    smsConsent: true,
    dataProcessingConsent: true,
    emergencyContactName: 'Anna Mustermann',
    emergencyContactRelation: 'Ehepartnerin',
    emergencyContactPhone: '+49 170 7654321',
    
    // Professional Information
    employmentStatus: 'employed',
    currentOccupation: 'Softwareentwickler',
    jobSector: 'it',
    yearsOfExperience: '10',
    desiredSalary: '4500',
    availableFrom: '2025-02-01',
    fullTimeAvailable: true,
    partTimeAvailable: false,
    shiftWorkAvailable: false,
    weekendWorkAvailable: false,
    willingToRelocate: true,
    educationLevel: 'bachelor',
    graduationYear: '2008',
    qualifications: [
      {
        id: 1,
        title: 'Fachinformatiker Anwendungsentwicklung',
        institution: 'IHK Berlin',
        completionDate: '2008-07-15',
        certificateNumber: 'FI-2008-12345'
      }
    ],
    employmentHistory: [
      {
        id: 1,
        employer: 'Tech Solutions GmbH',
        position: 'Senior Softwareentwickler',
        startDate: '2015-03-01',
        endDate: '',
        isCurrent: true
      },
      {
        id: 2,
        employer: 'Digital Innovations AG',
        position: 'Softwareentwickler',
        startDate: '2010-09-01',
        endDate: '2015-02-28',
        isCurrent: false
      }
    ],
    skills: 'JavaScript, React, Node.js, Python, SQL, Git, Agile',
    languages: 'Deutsch (Muttersprache), Englisch (Fließend), Spanisch (Grundkenntnisse)',
    drivingLicense: 'B'
  });

  const [documents] = useState([
    {
      id: 1,
      name: 'Lebenslauf_Max_Mustermann.pdf',
      type: 'application/pdf',
      size: 245678,
      uploadDate: '15.11.2025',
      uploadedBy: 'Max Mustermann',
      scanStatus: 'clean'
    },
    {
      id: 2,
      name: 'Zeugnis_Bachelor_Informatik.pdf',
      type: 'application/pdf',
      size: 1234567,
      uploadDate: '15.11.2025',
      uploadedBy: 'Max Mustermann',
      scanStatus: 'clean'
    },
    {
      id: 3,
      name: 'IHK_Zertifikat_Fachinformatiker.pdf',
      type: 'application/pdf',
      size: 876543,
      uploadDate: '15.11.2025',
      uploadedBy: 'Max Mustermann',
      scanStatus: 'scanning'
    }
  ]);

  const [versionHistory] = useState([
    {
      id: 1,
      timestamp: '26.11.2025 18:45',
      modifiedBy: 'Maria Schmidt',
      role: 'Sachbearbeiterin',
      changes: [
        { type: 'updated', field: 'Telefonnummer', oldValue: '+49 170 1234566', newValue: '+49 170 1234567' },
        { type: 'updated', field: 'Verfügbar ab', oldValue: '01.01.2025', newValue: '01.02.2025' }
      ],
      comment: 'Telefonnummer auf Kundenwunsch aktualisiert'
    },
    {
      id: 2,
      timestamp: '20.11.2025 14:30',
      modifiedBy: 'Thomas Weber',
      role: 'Teamleiter',
      changes: [
        { type: 'created', field: 'Qualifikation', newValue: 'Fachinformatiker Anwendungsentwicklung' }
      ],
      comment: 'Neue Qualifikation hinzugefügt nach Dokumentenprüfung'
    },
    {
      id: 3,
      timestamp: '15.11.2025 10:15',
      modifiedBy: 'Maria Schmidt',
      role: 'Sachbearbeiterin',
      changes: [
        { type: 'created', field: 'Profil', newValue: 'Erstellt' }
      ],
      comment: 'Initiale Profilerstellung'
    }
  ]);

  const [integrations] = useState([
    {
      id: 1,
      name: 'Sozialversicherung',
      description: 'Deutsche Rentenversicherung Bund',
      icon: 'Shield',
      status: 'synced',
      lastSync: '26.11.2025 18:30',
      recordCount: '1',
      pendingChanges: 0
    },
    {
      id: 2,
      name: 'Finanzamt',
      description: 'Bundeszentralamt für Steuern',
      icon: 'FileText',
      status: 'synced',
      lastSync: '26.11.2025 17:45',
      recordCount: '1',
      pendingChanges: 0
    },
    {
      id: 3,
      name: 'Krankenkasse',
      description: 'AOK - Die Gesundheitskasse',
      icon: 'Heart',
      status: 'syncing',
      lastSync: '26.11.2025 16:20',
      recordCount: '1',
      pendingChanges: 1
    },
    {
      id: 4,
      name: 'Jobcenter',
      description: 'Jobcenter Berlin Mitte',
      icon: 'Building',
      status: 'error',
      lastSync: '25.11.2025 09:15',
      recordCount: '0',
      pendingChanges: 2,
      errorMessage: 'Verbindung zum Server fehlgeschlagen. Bitte versuchen Sie es später erneut.'
    }
  ]);

  const [duplicates] = useState([
    {
      id: 'DUP-001',
      clientId: 'BA-2024-089456',
      firstName: 'Maximilian',
      lastName: 'Mustermann',
      dateOfBirth: '15.03.1985',
      phone: '+49 170 1234567',
      email: 'max.mustermann@email.de',
      address: 'Friedrichstraße 123, 10117 Berlin',
      matchScore: 92,
      matchingFields: ['Name (ähnlich)', 'Geburtsdatum', 'Telefonnummer', 'E-Mail', 'Adresse']
    }
  ]);

  const [applications] = useState([
    {
      id: 1,
      company: 'SAP SE',
      companyLocation: 'Walldorf',
      position: 'Senior Softwareentwickler',
      applicationDate: '10.11.2025',
      status: 'accepted',
      responseDate: '20.11.2025',
      rejectionReason: null
    },
    {
      id: 2,
      company: 'Siemens AG',
      companyLocation: 'München',
      position: 'IT-Spezialist',
      applicationDate: '05.11.2025',
      status: 'accepted',
      responseDate: '18.11.2025',
      rejectionReason: null
    },
    {
      id: 3,
      company: 'Deutsche Telekom AG',
      companyLocation: 'Bonn',
      position: 'Backend Developer',
      applicationDate: '12.11.2025',
      status: 'rejected',
      responseDate: '22.11.2025',
      rejectionReason: 'qualification_mismatch'
    },
    {
      id: 4,
      company: 'BMW Group',
      companyLocation: 'München',
      position: 'Fullstack Developer',
      applicationDate: '08.11.2025',
      status: 'rejected',
      responseDate: '19.11.2025',
      rejectionReason: 'no_interest_shown'
    },
    {
      id: 5,
      company: 'Volkswagen AG',
      companyLocation: 'Wolfsburg',
      position: 'Software Engineer',
      applicationDate: '15.11.2025',
      status: 'rejected',
      responseDate: '23.11.2025',
      rejectionReason: 'no_show_interview'
    },
    {
      id: 6,
      company: 'Bosch GmbH',
      companyLocation: 'Stuttgart',
      position: 'DevOps Engineer',
      applicationDate: '18.11.2025',
      status: 'pending',
      responseDate: null,
      rejectionReason: null
    },
    {
      id: 7,
      company: 'Allianz SE',
      companyLocation: 'München',
      position: 'IT-Consultant',
      applicationDate: '20.11.2025',
      status: 'pending',
      responseDate: null,
      rejectionReason: null
    },
    {
      id: 8,
      company: 'Deutsche Bank AG',
      companyLocation: 'Frankfurt',
      position: 'Java Developer',
      applicationDate: '03.11.2025',
      status: 'rejected',
      responseDate: '15.11.2025',
      rejectionReason: 'qualification_mismatch'
    }
  ]);

  const tabs = [
    { id: 'personal', label: 'Persönliche Informationen', icon: 'User' },
    { id: 'contact', label: 'Kontaktdaten', icon: 'Phone' },
    { id: 'professional', label: 'Berufliche Informationen', icon: 'Briefcase' },
    { id: 'applications', label: 'Bewerbungen', icon: 'FileText' },
    { id: 'documents', label: 'Dokumente', icon: 'Paperclip' },
    { id: 'history', label: 'Versionshistorie', icon: 'History' },
    { id: 'integrations', label: 'Integrationen', icon: 'Database' }
  ];

  useEffect(() => {
    // Auto-save functionality
    const autoSaveTimer = setTimeout(() => {
      handleAutoSave();
    }, 30000); // Auto-save every 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [formData]);

  useEffect(() => {
    // Keyboard shortcuts
    const handleKeyPress = (e) => {
      if ((e?.ctrlKey || e?.metaKey) && e?.key === 's') {
        e?.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [formData]);

  const handleAutoSave = () => {
    setLastSaved(new Date()?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }));
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Personal Information validation
    if (!formData?.salutation) newErrors.salutation = 'Anrede ist erforderlich';
    if (!formData?.firstName) newErrors.firstName = 'Vorname ist erforderlich';
    if (!formData?.lastName) newErrors.lastName = 'Nachname ist erforderlich';
    if (!formData?.dateOfBirth) newErrors.dateOfBirth = 'Geburtsdatum ist erforderlich';
    if (!formData?.placeOfBirth) newErrors.placeOfBirth = 'Geburtsort ist erforderlich';
    if (!formData?.nationality) newErrors.nationality = 'Staatsangehörigkeit ist erforderlich';
    if (!formData?.idNumber) newErrors.idNumber = 'Personalausweisnummer ist erforderlich';
    if (!formData?.socialSecurityNumber) newErrors.socialSecurityNumber = 'Sozialversicherungsnummer ist erforderlich';
    if (!formData?.maritalStatus) newErrors.maritalStatus = 'Familienstand ist erforderlich';
    if (!formData?.street) newErrors.street = 'Straße ist erforderlich';
    if (!formData?.postalCode) newErrors.postalCode = 'Postleitzahl ist erforderlich';
    if (!formData?.city) newErrors.city = 'Stadt ist erforderlich';
    if (!formData?.state) newErrors.state = 'Bundesland ist erforderlich';

    // Contact Details validation
    if (!formData?.preferredContact) newErrors.preferredContact = 'Bevorzugter Kontaktweg ist erforderlich';

    // Professional Information validation
    if (!formData?.employmentStatus) newErrors.employmentStatus = 'Beschäftigungsstatus ist erforderlich';
    if (!formData?.educationLevel) newErrors.educationLevel = 'Bildungsabschluss ist erforderlich';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      // Find first tab with errors
      const errorFields = Object.keys(errors);
      if (errorFields?.some(f => ['salutation', 'firstName', 'lastName', 'dateOfBirth', 'placeOfBirth', 'nationality', 'idNumber', 'socialSecurityNumber', 'maritalStatus', 'street', 'postalCode', 'city', 'state']?.includes(f))) {
        setActiveTab('personal');
      } else if (errorFields?.some(f => ['preferredContact']?.includes(f))) {
        setActiveTab('contact');
      } else if (errorFields?.some(f => ['employmentStatus', 'educationLevel']?.includes(f))) {
        setActiveTab('professional');
      }
      return;
    }

    setIsSaving(true);
    
    // Check for duplicates
    if (duplicates?.length > 0) {
      setShowDuplicateModal(true);
      setIsSaving(false);
      return;
    }

    // Simulate save
    setTimeout(() => {
      setIsSaving(false);
      setLastSaved(new Date()?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }));
      alert('Profil erfolgreich gespeichert!');
    }, 1500);
  };

  const handleExport = () => {
    alert('Export-Funktionalität wird implementiert');
  };

  const handleDelete = () => {
    if (window.confirm('Sind Sie sicher, dass Sie dieses Profil löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      alert('Profil gelöscht');
      navigate('/client-search-and-directory');
    }
  };

  const handleDuplicateMerge = (duplicateId) => {
    alert(`Profile werden zusammengeführt: ${duplicateId}`);
    setShowDuplicateModal(false);
  };

  const handleDuplicateIgnore = (duplicateId) => {
    alert(`Duplikat ignoriert: ${duplicateId}`);
  };

  const handleDocumentUpload = (files) => {
    alert(`${files?.length} Dokument(e) werden hochgeladen`);
  };

  const handleDocumentDelete = (docId) => {
    if (window.confirm('Möchten Sie dieses Dokument wirklich löschen?')) {
      alert(`Dokument gelöscht: ${docId}`);
    }
  };

  const handleDocumentDownload = (docId) => {
    alert(`Dokument wird heruntergeladen: ${docId}`);
  };

  const handleVersionRestore = (versionId) => {
    if (window.confirm('Möchten Sie diese Version wirklich wiederherstellen? Alle aktuellen Änderungen gehen verloren.')) {
      alert(`Version wiederhergestellt: ${versionId}`);
    }
  };

  const handleVersionCompare = (versionIds) => {
    alert(`Versionen werden verglichen: ${versionIds?.join(', ')}`);
  };

  const handleIntegrationSync = (integrationId) => {
    alert(`Synchronisation gestartet: ${integrationId}`);
  };

  const handleIntegrationDetails = (integrationId) => {
    alert(`Details anzeigen: ${integrationId}`);
  };

  const handleApplicationDetails = (applicationId) => {
    alert(`Bewerbungsdetails anzeigen: ${applicationId}`);
  };

  const handleAddApplication = () => {
    alert('Neue Bewerbung hinzufügen');
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <Header sidebarCollapsed={sidebarCollapsed} />
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="p-6 max-w-[1600px] mx-auto">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h1 className="text-3xl font-semibold text-foreground">Kundenprofil verwalten</h1>
                <p className="text-muted-foreground mt-1">
                  Umfassende Verwaltung von Kundendaten mit DSGVO-Konformität
                </p>
              </div>
              <div className="flex items-center gap-3">
                {lastSaved && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Check" size={16} className="text-success" />
                    <span>Zuletzt gespeichert: {lastSaved}</span>
                  </div>
                )}
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleExport}
                >
                  Exportieren
                </Button>
                <Button
                  variant="default"
                  iconName="Save"
                  iconPosition="left"
                  onClick={handleSave}
                  loading={isSaving}
                >
                  Speichern
                </Button>
              </div>
            </div>

            {/* Client Info Card */}
            <div className="bg-card rounded-lg border border-border p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="User" size={32} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {formData?.firstName} {formData?.lastName}
                  </h2>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                    <span className="flex items-center gap-1">
                      <Icon name="IdCard" size={14} />
                      Kunden-ID: BA-2025-123456
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      Erstellt: 15.11.2025
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="User" size={14} />
                      Sachbearbeiter: Maria Schmidt
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="px-3 py-1 bg-success/10 text-success rounded-full text-sm font-medium">
                  Aktiv
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDelete}
                  title="Profil löschen"
                >
                  <Icon name="Trash2" size={18} className="text-error" />
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="bg-card rounded-lg border border-border mb-6 overflow-x-auto">
            <div className="flex">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-card rounded-lg border border-border p-6">
            {activeTab === 'personal' && (
              <PersonalInfoTab
                formData={formData}
                errors={errors}
                onChange={handleFormChange}
                onValidate={(field, value) => {
                  // Real-time validation
                  if (errors?.[field]) {
                    setErrors(prev => ({ ...prev, [field]: '' }));
                  }
                }}
              />
            )}

            {activeTab === 'contact' && (
              <ContactDetailsTab
                formData={formData}
                errors={errors}
                onChange={handleFormChange}
              />
            )}

            {activeTab === 'professional' && (
              <ProfessionalInfoTab
                formData={formData}
                errors={errors}
                onChange={handleFormChange}
              />
            )}

            {activeTab === 'applications' && (
              <ApplicationTrackingPanel
                applications={applications}
                onViewDetails={handleApplicationDetails}
                onAddApplication={handleAddApplication}
              />
            )}

            {activeTab === 'documents' && (
              <DocumentAttachmentPanel
                documents={documents}
                onUpload={handleDocumentUpload}
                onDelete={handleDocumentDelete}
                onDownload={handleDocumentDownload}
              />
            )}

            {activeTab === 'history' && (
              <VersionHistoryPanel
                versions={versionHistory}
                onRestore={handleVersionRestore}
                onCompare={handleVersionCompare}
              />
            )}

            {activeTab === 'integrations' && (
              <IntegrationStatusPanel
                integrations={integrations}
                onSync={handleIntegrationSync}
                onViewDetails={handleIntegrationDetails}
              />
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex items-center justify-between">
            <Button
              variant="outline"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={() => navigate('/client-search-and-directory')}
            >
              Zurück zur Übersicht
            </Button>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  if (window.confirm('Möchten Sie alle Änderungen verwerfen?')) {
                    window.location?.reload();
                  }
                }}
              >
                Änderungen verwerfen
              </Button>
              <Button
                variant="default"
                iconName="Save"
                iconPosition="left"
                onClick={handleSave}
                loading={isSaving}
              >
                Speichern (Strg+S)
              </Button>
            </div>
          </div>

          {/* GDPR Compliance Notice */}
          <div className="mt-6 flex items-start gap-2 p-4 bg-accent/5 rounded-lg border border-accent/20">
            <Icon name="Shield" size={20} className="text-accent mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">DSGVO-Hinweis</p>
              <p>
                Alle Datenverarbeitungen erfolgen gemäß DSGVO und BDSG. Änderungen werden automatisch protokolliert. 
                Der Kunde hat jederzeit das Recht auf Auskunft, Berichtigung und Löschung seiner Daten.
              </p>
            </div>
          </div>
        </div>
      </main>
      {/* Duplicate Detection Modal */}
      {showDuplicateModal && (
        <DuplicateDetectionModal
          duplicates={duplicates}
          onClose={() => setShowDuplicateModal(false)}
          onMerge={handleDuplicateMerge}
          onIgnore={handleDuplicateIgnore}
        />
      )}
    </div>
  );
};

export default ClientProfileManagement;